import { format } from 'date-fns';
import { nanoid } from 'nanoid';
import { IConfig } from '../../config';
import { appAuthAtom, nonTradingDaysAtom, realtimeMarketsAtom, Store } from '../../state';
import {
  AuthState,
  ConnectionState,
  Currency,
  CurrencyCross,
  EStreamAllDataDTO,
  EStreamTradeDTO,
  getInstrumentIdForce,
  InstrumentListItem,
  InstrumentStore,
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  ListedInstrumentLike,
  QuoteFeedInterval,
  SSESource,
  SubscribeOptions,
  UserStreamMessage,
} from '../../types';
import { NbLogger, ObjectSet } from '../../utils';
import { CurrencyExchangeRateRT } from './currencyExchangeRateRT';
import { EStreamClient, GetSearchInstrumentListParams } from './eStreamClient';
import { EStreamConnection } from './eStreamConnection';
import { getRicForInstrumentData } from './eStreamUtils';
import { getInstrumentsToQuery } from './instrumentQueryUtils';
import { EventSourceLike } from './sseClient';
import { UserStreamClient } from './userStreamClient';
import { UserStreamConnection } from './userStreamConnection';

export interface ISSEManager {
  instanceId: number;
  isPrimaryInstanceSelected: boolean;
  eStreamConnectionState: ConnectionState | null;
  userStreamConnectionState: ConnectionState | null;

  broadcastSubscribeToInstruments(options: Array<SubscribeOptions>): void;

  broadcastUnsubscribeFromInstruments(options: Array<SubscribeOptions>): void;

  broadcastConnectionState(sseSource: SSESource, connectionState: ConnectionState): void;

  broadcastEStreamMessage(trades: Array<EStreamTradeDTO> | EStreamTradeDTO): void;
}

export abstract class SSECommunicatorBase {
  protected logger: NbLogger;

  /** SSE Connection instances */
  protected readonly sseConnections: [EStreamConnection, UserStreamConnection];

  /** SSE connection can be created if SSE manager decides, that this instance is the primary AND user can be logged in */
  protected sseConnectionRequestedEStream: boolean = false;

  /** SSE connection can be created if SSE manager decides, that this instance is the primary AND user can be logged in */
  protected sseConnectionRequestedUserStream: boolean = false;

  /** Marks if this instance is the primary instance, meaning this handles SSE connection */
  protected abstract set thisInstanceIsPrimary(value: boolean);

  /** Marks if this instance is the primary instance, meaning this handles SSE connection */
  protected abstract get thisInstanceIsPrimary(): boolean;

  protected get authState() {
    return this.store.get(appAuthAtom);
  }

  /**
   * Timeout for throttled eStream subscribe
   * @protected
   */
  protected throttledInstrumentSubscribeTimeout?: NodeJS.Timer;

  /**
   * Throttled list of instruments to subscribe or unsubscribe
   * @protected
   */
  protected subscriptionChanges: Array<{ direction: 'subscribe' | 'unsubscribe'; option: SubscribeOptions }> = [];

  protected constructor(
    protected offlineMode: boolean,
    protected createLogger: (name: string) => NbLogger,
    protected config: IConfig,
    protected store: Store,
    protected sseManager: ISSEManager,
    protected instrumentStore: InstrumentStore,
    protected eStreamClientConstructor: (token: string) => EventSourceLike,
    protected userStreamClientConstructor: () => EventSourceLike,
  ) {
    this.logger = createLogger('SSECommunicator');
    const eStreamClient = new EStreamClient(
      eStreamClientConstructor,
      store,
      createLogger(`EStreamClient`),
      null,
      config,
      new CurrencyExchangeRateRT(),
    );
    const eStreamConnection = new EStreamConnection(
      offlineMode,
      createLogger('EStreamConnection'),
      this.dispatchSSEStateUpdateEvent(SSESource.EStream).bind(this),
      config,
      this.onEStreamTradesReceived.bind(this),
      eStreamClient,
      store,
    );
    const userStreamClient = new UserStreamClient(
      userStreamClientConstructor,
      store,
      createLogger(`UserStreamClient`),
      config,
    );
    const userStreamConnection = new UserStreamConnection(
      offlineMode,
      createLogger('UserStreamConnection'),
      this.dispatchSSEStateUpdateEvent(SSESource.UserStream).bind(this),
      userStreamClient,
      config,
      this.onUserStreamMessageReceivedInternal.bind(this),
    );
    this.sseConnections = [eStreamConnection, userStreamConnection];
  }

  protected abstract dispatchSSEStateUpdateEvent(
    source: SSESource,
  ): (connectionState: ConnectionState, broadcastConnectionState: boolean) => void;

  get eStreamConnection(): EStreamConnection {
    return this.sseConnections[0];
  }

  get userStreamConnection(): UserStreamConnection {
    return this.sseConnections[1];
  }

  get realtimeMarkets() {
    return this.store.get(realtimeMarketsAtom);
  }

  get nonTradingDays() {
    return this.store.get(nonTradingDaysAtom);
  }

  abstract destroy(): void;

  abstract disconnect(force?: boolean): Promise<void>;

  async connectSSEClients(jwtToken: string, mode: SSESource): Promise<void> {
    if (!this.thisInstanceIsPrimary) {
      return;
    }

    this.logger.info(`connectSSEClients initiated from login`);
    if (mode === SSESource.EStream) {
      this.sseConnectionRequestedEStream = true;
    }
    if (mode === SSESource.UserStream) {
      this.sseConnectionRequestedUserStream = true;
      this.userStreamConnection.setToken(jwtToken);
    }
    this.handleSSEEvents();
  }

  subscribeToInstrumentChanges(
    options: Array<SubscribeOptions>,
    onInstrumentChange?: (items: ObjectSet<InstrumentStoreItem>) => void,
  ) {
    const deregisterInstrumentStoreChanges = this.instrumentStore.registerAll(
      options.map(o => o.instrument),
      onInstrumentChange,
    );

    if (this.thisInstanceIsPrimary) {
      this.handleNewSubscriptionsThrottled(options);

      return () => {
        this.removeEStreamSubscriptionsDeferred(options, deregisterInstrumentStoreChanges);
      };
    } else {
      this.sseManager.broadcastSubscribeToInstruments(options);
      return () => {
        this.sseManager.broadcastUnsubscribeFromInstruments(options);
        deregisterInstrumentStoreChanges();
      };
    }
  }

  async disconnectEStream() {
    this.eStreamConnection.disconnectSSEClient(true);
  }

  async disconnectUserStream() {
    this.userStreamConnection.disconnectSSEClient(true);
  }

  async connectEStreamSilent(refreshMarketRights?: boolean) {
    if (!this.thisInstanceIsPrimary) {
      return;
    }
    if (refreshMarketRights) {
      this.eStreamConnection.refreshMarketRights();
      this.instrumentStore.cleanup();
    }
    this.eStreamConnection.connectSSEClient({ isSilent: true });
  }

  getChartData(ric: string, interval: QuoteFeedInterval, from: Date, to: Date, abortController?: AbortController) {
    return this.eStreamConnection.getChartData(ric, interval, from, to, abortController);
  }

  /**
   * @returns All available entries for currently open session (1 minute tick)
   */
  public getCachedIntraDayChart(ric: string, abortController?: AbortController) {
    return this.eStreamConnection.getCachedIntraDayChart(ric, abortController);
  }

  /**
   * @returns All available entries max 20 years back (1 day tick)
   */
  public getCachedInterDayChart(ric: string, abortController?: AbortController) {
    return this.eStreamConnection.getCachedInterDayChart(ric, abortController);
  }

  /**
   * Load instrument data from EStream REST API
   * If an instrument is already exists in the store and has a subscription, it will not be queried again
   *
   * @param pInstruments
   * @param dataScope
   */
  async loadAndUpdateInstrumentWithSnapshot(
    pInstruments: Array<ListedInstrument | ListedInstrumentLike>,
    dataScope: Array<InstrumentSubscriptionDataScope>,
    forceReload = false,
  ): Promise<ObjectSet<InstrumentStoreItem>> {
    if (pInstruments.length === 0) {
      return Promise.resolve(new ObjectSet<InstrumentStoreItem>(getInstrumentIdForce));
    }

    try {
      const instruments = pInstruments.map(_ => ({ ..._, ric: getRicForInstrumentData(_.ric) }));

      const { instrumentsToQuery, result } = instruments.reduce(
        (acc, instrument) => {
          const instrumentStoreItem = this.instrumentStore.get(instrument);
          if (forceReload) {
            acc.instrumentsToQuery.push(instrument);
            return acc;
          }
          if (instrumentStoreItem && this.eStreamConnection.hasSubscription(instrument, dataScope)) {
            acc.result.add(instrumentStoreItem);
          } else {
            acc.instrumentsToQuery.push(instrument);
          }
          return acc;
        },
        {
          instrumentsToQuery: [] as Array<ListedInstrument | ListedInstrumentLike>,
          result: new ObjectSet<InstrumentStoreItem>(getInstrumentIdForce),
        },
      );

      if (instrumentsToQuery.length === 0 && !forceReload) {
        return result;
      }

      const data = await this.eStreamConnection.getInstrumentData(instrumentsToQuery, dataScope);

      const trades: Array<EStreamTradeDTO> = data.map(item => ({
        id: nanoid(),
        timestamp: new Date().toISOString(),
        ric: item.ric,
        mic: item.mic,
        ticker: (instruments.find(i => i.ric === item.ric) as ListedInstrument | undefined)?.ticker || '',
        trade: false,
        delayed: true,
        update_type: 'Snapshot',
        data: item,
      }));
      this.onEStreamTradesReceived(trades);
      trades.forEach(trade =>
        result.add(
          this.instrumentStore.setInstrumentData({
            trade,
            hasRealtimeAccess: this.hasRealtimePriceAccess(trade.mic),
            formatDate: this.formatDate,
            nonTradingDays: this.nonTradingDays,
            instrument: instruments.find(_ => getInstrumentIdForce(_) === getInstrumentIdForce(trade)) as
              | ListedInstrument
              | undefined,
          }),
        ),
      );

      return result;
    } catch (err) {
      this.logger.error(`Error getting instrument data for ${pInstruments.map(_ => _.ric).join(',')}`, err);
      throw err;
    }
  }

  async getExchangeRate(
    currencyCrosses: Array<{ base: Currency; quote: Currency }>,
  ): Promise<Map<CurrencyCross, number>> {
    return this.eStreamConnection.getExchangeRate(currencyCrosses);
  }

  /**
   * Raw passthrough to the eStream /search endpoint (quick product list with near-realtime, card-relevant data).
   */
  searchInstruments(params: GetSearchInstrumentListParams): Promise<Array<InstrumentListItem>> {
    return this.eStreamConnection.search(params);
  }

  /**
   * Loads instrument data from the eStream /search endpoint and writes it into the instrument store,
   * but ONLY for instruments that currently have no active subscription — so a real-time value is
   * never overwritten by the (delayed) search snapshot.
   *
   * Unlike {@link loadAndUpdateInstrumentWithSnapshot}, the write uses a non-`Snapshot` update_type, so
   * repeated polling actually refreshes the values (the snapshot merge would otherwise keep the old value).
   */
  async loadInstrumentsViaSearch(
    pInstruments: Array<ListedInstrument>,
    dataScope: Array<InstrumentSubscriptionDataScope>,
  ): Promise<Array<InstrumentListItem>> {
    if (pInstruments.length === 0) {
      return [];
    }

    try {
      const instruments = pInstruments.map(instr => ({ ...instr, ric: getRicForInstrumentData(instr.ric) }));
      const instrumentByRic = new Map(instruments.map(instr => [instr.ric, instr]));
      const searchItems = await this.eStreamConnection.search({ rics: instruments.map(instr => instr.ric) });

      searchItems.forEach(item => {
        const instrument = instrumentByRic.get(item.ric);
        if (!instrument) {
          return;
        }
        if (this.hasSubscriptionCoveringOrPending(instrument, dataScope)) {
          return;
        }

        const mic = item.mic ?? instrument.mic;
        const data = { ...item.fields, ric: item.ric, mic } as unknown as EStreamAllDataDTO;
        const trade: EStreamTradeDTO = {
          id: nanoid(),
          timestamp: new Date().toISOString(),
          ric: item.ric,
          mic,
          ticker: instrument.ticker || '',
          trade: false,
          delayed: !item.realtime,
          // non-Snapshot, so repeated polling overwrites/refreshes the stored value
          update_type: 'Unspecified',
          isPolled: true,
          data,
        };

        this.instrumentStore.setInstrumentData({
          trade,
          hasRealtimeAccess: item.realtime,
          formatDate: this.formatDate,
          nonTradingDays: this.nonTradingDays,
          instrument,
        });
      });

      return searchItems;
    } catch (err) {
      this.logger.error(`Error searching instrument data for ${pInstruments.map(instr => instr.ric).join(',')}`, err);
      throw err;
    }
  }

  /**
   * Returns true if the instrument has an eStream subscription covering all the requested data scopes,
   * or such a subscription has already been requested, but is still waiting for the throttled flush.
   *
   * The pending part matters because {@link handleNewSubscriptionsThrottled} only registers the subscription
   * after `eStreamSubscribeThrottleTimeout`. A search poll answering within that window would otherwise
   * stamp the instrument as polled, hiding the real-time price indicator until the next poll.
   */
  protected hasSubscriptionCoveringOrPending(
    instrument: ListedInstrument | ListedInstrumentLike,
    dataScope: Array<InstrumentSubscriptionDataScope>,
  ): boolean {
    if (this.eStreamConnection.hasSubscriptionCovering(instrument, dataScope)) {
      return true;
    }

    return this.subscriptionChanges.some(
      ({ direction, option }) =>
        direction === 'subscribe' &&
        getInstrumentIdForce(option.instrument) === getInstrumentIdForce(instrument) &&
        dataScope.every(scope => option.dataScope.includes(scope)),
    );
  }

  protected removeEStreamSubscriptionsDeferred(
    options: Array<SubscribeOptions>,
    deregisterInstrumentStoreChanges: () => void,
  ) {
    this.subscriptionChanges.push(
      ...options.map(
        option =>
          ({
            direction: 'unsubscribe',
            option,
          }) as const,
      ),
    );

    // instrument store listeners can be deregistered immediately, those components will be unmounted anyway
    deregisterInstrumentStoreChanges();

    if (!this.throttledInstrumentSubscribeTimeout) {
      this.throttledInstrumentSubscribeTimeout = setTimeout(
        this.handleEStreamSubscriptionChangesLater.bind(this),
        this.config.SSE.eStreamSubscribeThrottleTimeout,
      );
    }
  }

  /**
   * Called by SSEManager when this instance is selected as the primary one
   */
  protected onThisInstanceSelectedAsPrimary(eStreamSubscriptions?: Array<SubscribeOptions>) {
    this.logger.info(`This instance has been selected as primary`, eStreamSubscriptions);
    this.thisInstanceIsPrimary = true;
    this.userStreamConnection.setAsPrimaryInstance();
    this.eStreamConnection.setAsPrimaryInstance();
    this.sseConnectionRequestedUserStream = true;
    this.sseConnectionRequestedEStream = true;

    if (eStreamSubscriptions) {
      this.eStreamConnection.addEStreamSubscriptions(eStreamSubscriptions, this.sseManager.instanceId);
    }

    this.handleSSEEvents();
  }

  /**
   * Handles the internal SSE connection state change. Broadcast the connection state when necessary
   *
   * @param {SSESource} source - The source of the SSE connection state change.
   * @param {ConnectionState} connectionState - The new connection state.
   * @param {boolean} broadcastConnectionState - Indicates whether to broadcast the connection state.
   */
  protected onSSEConnectionStateChangeInternal(
    source: SSESource,
    connectionState: ConnectionState,
    broadcastConnectionState: boolean,
  ) {
    if (connectionState !== ConnectionState.Connected) {
      switch (source) {
        case SSESource.EStream:
          this.sseConnectionRequestedEStream = false;
          break;
        case SSESource.UserStream:
          this.sseConnectionRequestedUserStream = false;
          break;
      }
    }

    // broadcast the connection state
    if (
      this.thisInstanceIsPrimary &&
      this.sseManager.isPrimaryInstanceSelected &&
      this.authState?.isAuthenticated &&
      this.authState?.authStatus === AuthState.Authenticated &&
      broadcastConnectionState
    ) {
      this.sseManager.broadcastConnectionState(SSESource.EStream, connectionState);
    }

    // update sse manager's connection state
    switch (source) {
      case SSESource.EStream:
        this.sseManager.eStreamConnectionState = connectionState;
        break;
      case SSESource.UserStream:
        this.sseManager.userStreamConnectionState = connectionState;
        break;
    }
  }

  /**
   * Handles the EStream message:
   *  - updates the local instrument store
   *  - if this is the primary instance, we broadcast the message to other instances
   * @param trades
   * @protected
   */
  protected onEStreamTradesReceived(trades: Array<EStreamTradeDTO> | EStreamTradeDTO) {
    this.instrumentStore.setMany(trades, this.hasRealtimePriceAccess.bind(this), this.formatDate, this.nonTradingDays);
    // if we are the primary instance we need other instances to know about the EStream messages
    if (this.thisInstanceIsPrimary) {
      this.sseManager.broadcastEStreamMessage(trades);
    }
  }

  protected abstract onUserStreamMessageReceivedInternal(message: UserStreamMessage): void;

  /**
   * When there is an EStream subscription change, we need to update the subscription on the primary instance
   * @param dir subscribe/unsubscribe to an instrument
   * @param options the instrument to subscribe/unsubscribe
   * @param instanceId the instance id which initiated the change
   */
  protected onEStreamSubscriptionChange(
    dir: 'subscribe' | 'unsubscribe',
    options: Array<SubscribeOptions>,
    instanceId: number,
  ) {
    if (this.thisInstanceIsPrimary) {
      if (dir === 'subscribe') {
        this.eStreamConnection.addEStreamSubscriptions(options, instanceId);
      } else {
        this.eStreamConnection.removeEStreamSubscriptions(options, instanceId);
      }
    }
  }

  /**
   * When SSEManager decides we need to be the primary instance, we try to connect to EStream if we need to
   * @protected
   */
  protected handleSSEEvents() {
    if (this.thisInstanceIsPrimary) {
      if (this.sseConnectionRequestedEStream) {
        this.eStreamConnection.connectSSEClient({});
      }
      if (this.sseConnectionRequestedUserStream) {
        this.userStreamConnection.connectSSEClient({});
      }
    }
  }

  /**
   * Adds the subscribed instruments to the throttled list, and starts the throttled query if it's not yet started
   * @param options subscribe option
   * @protected
   */
  protected handleNewSubscriptionsThrottled(options: Array<SubscribeOptions>) {
    this.subscriptionChanges.push(
      ...options.map(
        option =>
          ({
            direction: 'subscribe',
            option,
          }) as const,
      ),
    );

    if (!this.throttledInstrumentSubscribeTimeout) {
      this.throttledInstrumentSubscribeTimeout = setTimeout(
        this.handleEStreamSubscriptionChangesLater.bind(this),
        this.config.SSE.eStreamSubscribeThrottleTimeout,
      );
    }
  }

  /**
   * Updates the EStream subscriptions and triggers the instrument store change listeners
   *
   * @protected
   */
  protected async handleEStreamSubscriptionChangesLater() {
    this.throttledInstrumentSubscribeTimeout = undefined;
    if (this.authState?.authStatus !== AuthState.Authenticated) {
      // this code can be called from useEffect cleanups, but in this case we don't have to unsubscribe
      // we are already disconnected, so just a cleanup is needed
      this.subscriptionChanges.splice(0);
      this.instrumentStore.cleanup();
      return;
    }

    const {
      alreadySubscribedInstruments,
      newlyRequestedInstruments,
      addedSubscriptions,
      unsubscribeList,
      forceRequestStaticData,
    } = getInstrumentsToQuery(this.subscriptionChanges, this.eStreamConnection.subscriptions);

    this.eStreamConnection.addEStreamSubscriptions(addedSubscriptions.values(), this.sseManager.instanceId, false);
    this.eStreamConnection.removeEStreamSubscriptions(unsubscribeList.values(), this.sseManager.instanceId, true);

    if (forceRequestStaticData.instruments.length > 0) {
      this.loadAndUpdateInstrumentWithSnapshot(forceRequestStaticData.instruments, forceRequestStaticData.dataScope);
    }

    // once we subscribed to the instruments, we can clear the subscription change list
    this.subscriptionChanges.splice(0);

    this.instrumentStore.triggerChangeListeners(alreadySubscribedInstruments);
    this.logger.debug(`Querying current instrument data for new subscriptions: ${newlyRequestedInstruments.join(',')}`);
  }

  protected hasRealtimePriceAccess(market: string): boolean {
    return this.realtimeMarkets.includes(market);
  }

  /** Format date to a predefined string format, used for parsing and checking with non trading days */
  protected formatDate(date: Date) {
    return format(date, 'yyyy.MM.dd');
  }
}
